import React from 'react';
import { observer } from 'mobx-react-lite';

import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';

import CardLoggedOut from 'components/CardLoggedOut';
import LoadingScreenBig from 'components/LoadingScreenBig';
import useRoles from 'hooks/useRoles';
import { UserProps } from 'stores/user_store';

type AssistantManagerOnlyProps = {
    currentUser: UserProps,
    loaded: boolean,
    isAssistant: boolean,
    isManager: boolean,
    children: React.ReactNode,
};

// Clients can use Day Ones, but assistants (is_beta) have to be managers.
// Assistants without manager access get a polite "not for you"
// card with a way to log out.
export function AssistantManagerOnly({ currentUser, loaded, isAssistant, isManager, children }: AssistantManagerOnlyProps) {
    if (!loaded) {
        return <LoadingScreenBig message="Checking your access..." />;
    }

    if (!isAssistant || isManager) {
        return <>{children}</>;
    }

    const name = currentUser.first_name || currentUser.name || currentUser.primary_email;

    return (
        <CardLoggedOut>
            <Divider />
            <Stack spacing={1} my={2}>
                <Typography level="title-lg">
                    Sorry{name ? `, ${name}` : ''}. Assistants need manager access to use Day Ones.
                </Typography>
                <Typography level="body-md">
                    {currentUser.primary_email} doesn't have manager access.
                </Typography>
            </Stack>
            <Stack direction="row" justifyContent="center">
                {/* a plain Button: index.css forces every <a> to primary blue */}
                <Button onClick={() => window.location.assign('/logout')}>Log out</Button>
            </Stack>
        </CardLoggedOut>
    );
}

const AssistantManagerGate = observer(({ currentUser, children }: { currentUser: UserProps, children: React.ReactNode }) => {
    const { rolesLoaded, hasRole } = useRoles();
    return (
        <AssistantManagerOnly
            currentUser={currentUser}
            loaded={rolesLoaded}
            isAssistant={currentUser.is_beta}
            isManager={hasRole('manager')}
        >
            {children}
        </AssistantManagerOnly>
    );
});

export default AssistantManagerGate;
